import { useQuery, useLazyQuery, gql } from "@apollo/client"
import * as Sentry from "@sentry/browser"

const ORGANISATION_TEMPLATES_LIMIT = 10

const PROCESS_FIELDS = `
  id
  name
  description
  steps {
    id
    name
    responseType
    description
    uploads { id key fileName fileGroup fileSize }
  }
  statusSet
  after { year month day hour minute }
  due { year month day hour minute }
  repeat { 
    type
    frequency
    weekdays { 
      monday tuesday wednesday thursday friday saturday sunday 
    }
    months { 
      january february march april may june july august september october november december
    }
  }
  users { id fullName firstName lastName avatar { key } }
  groups { id name }
  author { id fullName firstName lastName avatar { key } }
  pinning { type period }
  inactive
  locations { id name }
  createdAt
  updatedAt
`

const TASK_FIELDS = `
  id
  name
  description      
  priority
  statusSet
  after { year month day hour minute }
  due { year month day hour minute }
  pinned
  inactive
  users { id fullName firstName lastName avatar { key } }
  groups { id name }
  uploads { id key fileName fileGroup fileSize }
  author { id fullName firstName lastName avatar { key } }
  locations { id name }
  createdAt
  updatedAt
`

const ORGANISATION_TEMPLATES_QUERY = gql`
  query OrganisationTemplates (
    $set: String!, 
    $locations: [ID!], 
    $groups: [ID!], 
    $schedules: [ScheduleFilter!], 
    $searchText: String, 
    $limit: Int = ${ORGANISATION_TEMPLATES_LIMIT}, 
    $offset: Int) {
      organisationTemplates(set: $set, locations: $locations, groups: $groups, schedules: $schedules, searchText: $searchText, limit: $limit, offset: $offset) {
        items {
          ... on Process {
            __typename
            ${PROCESS_FIELDS}
          }
          ... on Task {
            __typename
            ${TASK_FIELDS}
          }
        }
        total
      } 
    }
`

const ORGANISATION_TEMPLATE_QUERY = gql`query OrganisationTemplate ($id: ID!){
  organisationTemplate(id: $id) {
    ... on Process {
      __typename
      ${PROCESS_FIELDS}
    }
    ... on Task {
      __typename
      ${TASK_FIELDS}
    }
  }
}`

const loadMore = (result) => async () => {
  try {
    await result.fetchMore({
      variables: {
        ...result.variables,
        offset: result.data.organisationTemplates.items.length,
        limit: ORGANISATION_TEMPLATES_LIMIT,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev
        return {
          ...prev,
          organisationTemplates: {
            items: [...prev.organisationTemplates.items, ...fetchMoreResult.organisationTemplates.items],
            total: fetchMoreResult.organisationTemplates.total,
            __typename: prev.organisationTemplates.__typename,
          },
        }
      },
    })
  } catch (error) {
    Sentry.captureException(error)
  }
}

const useQueryOrganisationTemplates = (options) =>
  useQuery(ORGANISATION_TEMPLATES_QUERY, {
    fetchPolicy: "cache-and-network",
    ...options,
  })

const useLazyQueryOrganisationTemplates = () => {
  const [loadItems, result] = useLazyQuery(ORGANISATION_TEMPLATES_QUERY, {
    variables: {
      offset: 0,
      limit: ORGANISATION_TEMPLATES_LIMIT,
    },
    fetchPolicy: "cache-and-network",
  })

  return [
    loadItems,
    {
      ...result,
      loadMore: loadMore(result),
    },
  ]
}

const queryOrganisationTemplate = async (id, client) => {
  try {
    const response = await client.query({
      query: ORGANISATION_TEMPLATE_QUERY,
      variables: { id },
    })
    return response
  } catch (err) {
    return err
  }
}

const processTypePolicies = {
  Process: {
    fields: {
      repeat: {
        merge(prev, next) {
          return { ...next }
        },
      },
    },
  },
}

export {
  useQueryOrganisationTemplates,
  useLazyQueryOrganisationTemplates,
  queryOrganisationTemplate,
  PROCESS_FIELDS,
  TASK_FIELDS,
  processTypePolicies,
}
